import axios, { AxiosInstance, CancelTokenSource } from 'axios';

import {
  Direction,
  Realm,
  Connection,
  Action,
  SObject,
  CompositeKey,
  Aligned,
  AlignedSourceAware,
} from '@/types';

import {
  ListValueFieldsResponse,
  ValueField,
  ValueFieldError,
  FilterRecord,
  CompareOptions,
} from './SalesforceService.types';

export interface GetConnectionsResponse {
  source: Connection | null;
  target: Connection | null;
}

export interface PageToken {
  lastKey?: CompositeKey;
}

export type RecordKey = string;
export type RecordId = `${SObject}:${RecordKey}`;

export type CompareOverviewRecord = {
  Id: RecordId;
  Action: Action;
  Key: RecordKey;
  SObject: SObject;
  Name: string;
  Aligned: Aligned;
};

export type SourceAwareOverviewRecord = Omit<
  CompareOverviewRecord,
  'Aligned'
> & { Aligned: AlignedSourceAware };

export type RootDependencyRecord = {
  Id: RecordId;
  Key: RecordKey;
  SObject: SObject;
  Name: string;
  Exists: boolean;
  IsDuplicate: boolean;
  IsRequired: boolean;
} & { Parent?: undefined };

export type DependencyRecordWithParent = Omit<
  RootDependencyRecord,
  'Parent'
> & {
  Parent: RootDependencyRecord;
};

export type DependencyRecord =
  | RootDependencyRecord
  | DependencyRecordWithParent;

export interface CompareResponse {
  records: CompareOverviewRecord[];
  nextPageToken?: PageToken;
}

type DuplicateId = string;
export interface DuplicateRecord {
  Key: string;
  Name: string;
  Identifiers: DuplicateId[];
}

export interface DuplicatesResponse {
  sourceDuplicates: DuplicateRecord[];
  targetDuplicates: DuplicateRecord[];
}

export interface IsPackageInstalledResponse {
  isInstalledInSource: boolean;
  isInstalledInTarget: boolean;
}

const ValueFieldErrorTranslations = {
  [ValueFieldError.MISSING_IN_SOURCE]: 'Field not present in source',
  [ValueFieldError.MISSING_IN_TARGET]: 'Field not present in target',
  [ValueFieldError.INACCESSIBLE_IN_SOURCE]: 'Field not accessible in source',
  [ValueFieldError.INACCESSIBLE_IN_TARGET]: 'Field not accessible in target',
  [ValueFieldError.UNWRITABLE_IN_TARGET]: 'Field not writable in target',
};

export interface UserSettingsResponse {
  productKey: string[];
  productKeyOptions: string[];
  quoteTermKey: string[];
  quoteTermKeyOptions: string[];
  onlyActiveProducts: boolean;
}

export type UserSettings = {
  productKey: string[];
  quoteTermKey: string[];
  onlyActiveProducts: boolean;
};

export interface GetChildObjectsResponse {
  children: SObject[];
}

export interface SearchResultPromise {
  cancelTokenSource: CancelTokenSource;
  promise: Promise<{
    records: CompareOverviewRecord[];
  }>;
}

class SalesforceService {
  private readonly client: AxiosInstance;

  constructor(client: AxiosInstance) {
    this.client = client;
  }

  refreshConnections(): Promise<GetConnectionsResponse> {
    return this.client
      .get<GetConnectionsResponse>('/apis/salesforce/v1/refresh-connections')
      .then(({ data }) => data);
  }

  getConnections(): Promise<GetConnectionsResponse> {
    return this.client
      .get<GetConnectionsResponse>('/apis/salesforce/v1/connections')
      .then(({ data }) => data);
  }

  async disconnect(direction: Direction): Promise<void> {
    await this.client.delete(`/apis/salesforce/v1/disconnect/${direction}`);
  }

  compare(
    sobject: SObject,
    nextPageToken?: PageToken,
    options: CompareOptions = {}
  ): {
    promise: Promise<CompareResponse>;
    cancelTokenSource: CancelTokenSource;
  } {
    const cancelTokenSource = axios.CancelToken.source();

    const promise = this.client
      .get<CompareResponse>('/apis/salesforce/v1/compare', {
        params: {
          sobject,
          nextPageToken,
          customValueFields: options.customValueFields,
        },
        cancelToken: cancelTokenSource.token,
      })
      .then(({ data }) => data);

    return {
      promise,
      cancelTokenSource,
    };
  }

  compareOne(
    sobject: SObject,
    serializedCompositeKey: string,
    customValueFields: string[]
  ): Promise<CompareOverviewRecord> {
    return this.client
      .get<CompareOverviewRecord>('/apis/salesforce/v1/compare-one', {
        params: {
          sobject,
          key: serializedCompositeKey,
          customValueFields,
        },
      })
      .then(({ data }) => data);
  }

  getRequiredDependencies(aligned: AlignedSourceAware): {
    promise: Promise<DependencyRecord[]>;
    cts: CancelTokenSource;
  } {
    const cts = axios.CancelToken.source();

    const promise = this.client
      .post<DependencyRecord[]>(
        '/apis/salesforce/v1/dependencies',
        {
          aligned,
        },
        { cancelToken: cts.token }
      )
      .then(({ data }) => data);

    return {
      promise,
      cts,
    };
  }

  async getRootSObjects(): Promise<SObject[]> {
    const { data } = await this.client.get<SObject[]>(
      '/apis/salesforce/v1/root-sobjects'
    );
    return data;
  }

  async getDuplicates(sobject: SObject): Promise<DuplicatesResponse> {
    const { data } = await this.client.get<DuplicatesResponse>(
      `/apis/salesforce/v1/duplicates/${sobject}`
    );
    return data;
  }

  async isPackageInstalled(
    subscriberPackageId: string
  ): Promise<IsPackageInstalledResponse> {
    const { data } = await this.client.get<IsPackageInstalledResponse>(
      `/apis/salesforce/v1/is-package-installed/${subscriberPackageId}`
    );
    return data;
  }

  async getValueFields(rootObject: SObject): Promise<ValueField[]> {
    const { data } = await this.client.get<ListValueFieldsResponse>(
      `/apis/salesforce/v1/value-fields/${rootObject}`
    );

    return data.map(({ fullyQualifiedName, sobject, field, errors }) => ({
      fullyQualifiedName,
      sobject,
      field,
      errors: errors.map((error) => ValueFieldErrorTranslations[error]),
    }));
  }

  static getConnectionURL(direction: Direction, realm: Realm): string {
    return `apis/salesforce/v1/authorize/${direction}/${realm.toLowerCase()}`;
  }

  async getUserSettings(): Promise<UserSettingsResponse> {
    const { data } = await this.client.get<UserSettingsResponse>(
      `/apis/salesforce/v1/user-settings`
    );

    return data;
  }

  async saveUserSettings(
    userSettings: UserSettings
  ): Promise<UserSettingsResponse> {
    const { data } = await this.client.post<UserSettingsResponse>(
      `/apis/salesforce/v1/user-settings`,
      { userSettings }
    );

    return data;
  }

  async getFilters(sobject: SObject): Promise<FilterRecord[]> {
    const { data } = await this.client.get<FilterRecord[]>(
      `/apis/salesforce/v1/filters`,
      { params: { sobject } }
    );

    return data;
  }

  getChildObjects(sobject: SObject): {
    cancelTokenSource: CancelTokenSource;
    promise: Promise<SObject[]>;
  } {
    const cancelTokenSource = axios.CancelToken.source();

    const promise = this.client
      .get<GetChildObjectsResponse>(`/apis/salesforce/v1/plan/${sobject}`, {
        cancelToken: cancelTokenSource.token,
      })
      .then(({ data }) => data.children);

    return {
      cancelTokenSource,
      promise,
    };
  }

  search(
    sobject: SObject,
    query: string,
    options: CompareOptions = {}
  ): SearchResultPromise {
    const cancelTokenSource = axios.CancelToken.source();

    const promise = this.client
      .get<{ records: CompareOverviewRecord[] }>(`/apis/salesforce/v1/search`, {
        params: {
          sobject,
          query,
          customValueFields: options.customValueFields,
        },
        cancelToken: cancelTokenSource.token,
      })
      .then(({ data }) => data);

    return {
      cancelTokenSource,
      promise,
    };
  }

  async getRequiredDependencyStatusUpdate(
    sobject: SObject,
    key: string,
    name: string
  ): Promise<DependencyRecord> {
    const { data } = await this.client.get<DependencyRecord>(
      '/apis/salesforce/v1/dependency-status',
      {
        params: {
          sobject,
          key,
          name,
        },
      }
    );

    return data;
  }
}

export default SalesforceService;
